import type Phaser from 'phaser';
import { UNIT_TYPES, type UnitTypeId } from '../sim/units/unitDefs';
import { ART_SCALE } from './proceduralArt';

/**
 * TEMPORARY PLACEHOLDER ART — original survivor-soldier figures drawn at boot
 * with Canvas 2D (no external assets). Each texture's origin is the unit's
 * feet, so a sprite sits exactly on its grid position.
 */
export interface UnitTextureInfo {
  readonly key: string;
  readonly originX: number;
  readonly originY: number;
  /** World px from the feet to the top of the head (for health bars and hit tests). */
  readonly height: number;
  /** Half the body width in world px. */
  readonly halfWidth: number;
}

export const UNIT_RING_KEY = 'unit-ring';
/** Units are drawn this much larger than their texture's base size (readable at 100 % zoom). */
export const UNIT_SCALE = 1.25;
const W = 24;
const H = 36;
const FEET_Y = 33;

export function unitTextureKey(type: UnitTypeId): string {
  return `unit-${type}`;
}

export function generateUnitArt(scene: Phaser.Scene): Map<UnitTypeId, UnitTextureInfo> {
  const out = new Map<UnitTypeId, UnitTextureInfo>();
  for (const type of UNIT_TYPES) {
    const key = unitTextureKey(type);
    if (!scene.textures.exists(key)) {
      const tex = scene.textures.createCanvas(key, W * ART_SCALE, H * ART_SCALE);
      if (!tex) throw new Error(`Could not create texture ${key}`);
      const ctx = tex.getContext();
      ctx.scale(ART_SCALE, ART_SCALE);
      DRAW[type](ctx);
      tex.refresh();
    }
    out.set(type, { key, originX: 0.5, originY: FEET_Y / H, height: (FEET_Y - 2) * UNIT_SCALE, halfWidth: (type === 'heavy' ? 8 : 6.5) * UNIT_SCALE });
  }
  if (!scene.textures.exists(UNIT_RING_KEY)) {
    const tex = scene.textures.createCanvas(UNIT_RING_KEY, 28 * ART_SCALE, 14 * ART_SCALE);
    if (!tex) throw new Error('Could not create unit ring');
    const ctx = tex.getContext();
    ctx.scale(ART_SCALE, ART_SCALE);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(14, 7, 12, 5.5, 0, 0, Math.PI * 2);
    ctx.stroke();
    tex.refresh();
  }
  return out;
}

/** Shared figure parts. `cx` is the body centre line. */
function shadow(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = 'rgba(10,12,10,0.35)';
  ctx.beginPath();
  ctx.ellipse(W / 2, FEET_Y, 8, 3, 0, 0, Math.PI * 2);
  ctx.fill();
}

function legs(ctx: CanvasRenderingContext2D, color: string, boot: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(W / 2 - 4, 22, 3.4, 9);
  ctx.fillRect(W / 2 + 0.6, 22, 3.4, 9);
  ctx.fillStyle = boot;
  ctx.fillRect(W / 2 - 4.5, 30, 4, 3);
  ctx.fillRect(W / 2 + 0.5, 30, 4, 3);
}

function head(ctx: CanvasRenderingContext2D, skin: string, y = 8): void {
  ctx.fillStyle = skin;
  ctx.beginPath();
  ctx.arc(W / 2, y, 3.6, 0, Math.PI * 2);
  ctx.fill();
}

const DRAW: Record<UnitTypeId, (ctx: CanvasRenderingContext2D) => void> = {
  ranger(ctx) {
    shadow(ctx);
    legs(ctx, '#6b6250', '#2c2a25');
    // Olive field jacket.
    ctx.fillStyle = '#5f6e43';
    ctx.fillRect(W / 2 - 5, 12, 10, 11);
    ctx.fillStyle = '#4c5935';
    ctx.fillRect(W / 2 - 5, 18, 10, 2);
    head(ctx, '#d1a77e');
    // Cap.
    ctx.fillStyle = '#3f4a2c';
    ctx.fillRect(W / 2 - 4, 3.5, 8, 3);
    ctx.fillRect(W / 2 - 1, 5.5, 6, 1.5);
    // Rifle across the body.
    ctx.strokeStyle = '#2a2622';
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(W / 2 - 7, 22);
    ctx.lineTo(W / 2 + 8, 11);
    ctx.stroke();
    ctx.strokeStyle = '#7a5434';
    ctx.lineWidth = 2.2;
    ctx.beginPath();
    ctx.moveTo(W / 2 - 7, 22);
    ctx.lineTo(W / 2 - 3, 19);
    ctx.stroke();
  },
  heavy(ctx) {
    shadow(ctx);
    legs(ctx, '#4d5157', '#222326');
    // Bulky plated torso.
    ctx.fillStyle = '#6c737b';
    ctx.fillRect(W / 2 - 7, 11, 14, 13);
    ctx.fillStyle = '#545a61';
    ctx.fillRect(W / 2 - 7, 16, 14, 2);
    ctx.fillRect(W / 2 - 1, 11, 2, 13);
    head(ctx, '#c79a73', 8);
    // Helmet with visor.
    ctx.fillStyle = '#3f444a';
    ctx.beginPath();
    ctx.arc(W / 2, 7.5, 4.4, Math.PI, 0);
    ctx.fill();
    ctx.fillStyle = '#c8642a';
    ctx.fillRect(W / 2 - 3.5, 7.5, 7, 1.5);
    // Riot shield.
    ctx.fillStyle = '#8e969d';
    ctx.fillRect(W / 2 + 4, 13, 5, 13);
    ctx.strokeStyle = '#2b2e32';
    ctx.lineWidth = 0.8;
    ctx.strokeRect(W / 2 + 4, 13, 5, 13);
  },
  engineer(ctx) {
    shadow(ctx);
    legs(ctx, '#3e4a5a', '#2a2622');
    // Hi-vis vest over overalls.
    ctx.fillStyle = '#3e4a5a';
    ctx.fillRect(W / 2 - 5, 12, 10, 11);
    ctx.fillStyle = '#e07a2a';
    ctx.fillRect(W / 2 - 5, 12, 3.5, 10);
    ctx.fillRect(W / 2 + 1.5, 12, 3.5, 10);
    ctx.fillStyle = '#f0e2a0';
    ctx.fillRect(W / 2 - 5, 17, 10, 1.2);
    head(ctx, '#b88762');
    // Hard hat.
    ctx.fillStyle = '#f0b43c';
    ctx.beginPath();
    ctx.arc(W / 2, 6.5, 4.2, Math.PI, 0);
    ctx.fill();
    ctx.fillRect(W / 2 - 5, 6, 10, 1.4);
    // Wrench.
    ctx.strokeStyle = '#a9b0b6';
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(W / 2 + 5, 22);
    ctx.lineTo(W / 2 + 9, 15);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(W / 2 + 9.3, 14.3, 1.6, 0, Math.PI * 2);
    ctx.stroke();
  },
  walker(ctx) {
    // Original infected figure: hunched, grey-green skin, torn clothes, arms reaching forward.
    shadow(ctx);
    // Uneven legs (one dragging).
    ctx.fillStyle = '#3d3a34';
    ctx.fillRect(W / 2 - 4, 22, 3.4, 9);
    ctx.fillRect(W / 2 + 1, 23, 3.4, 8);
    ctx.fillStyle = '#2a2722';
    ctx.fillRect(W / 2 - 4.5, 30, 4, 3);
    ctx.fillRect(W / 2 + 1, 30.5, 4, 2.5);
    // Torn shirt, leaning forward.
    ctx.fillStyle = '#6a6f5c';
    ctx.beginPath();
    ctx.moveTo(W / 2 - 5, 23);
    ctx.lineTo(W / 2 - 4, 13);
    ctx.lineTo(W / 2 + 6, 12);
    ctx.lineTo(W / 2 + 5, 23);
    ctx.lineTo(W / 2 + 2, 21.5);
    ctx.lineTo(W / 2 - 1, 23.5);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#7b2e2a';
    ctx.fillRect(W / 2 - 1, 15, 3, 2);
    ctx.fillRect(W / 2 + 2, 18, 2, 1.5);
    // Head, tilted forward.
    ctx.fillStyle = '#9aa784';
    ctx.beginPath();
    ctx.arc(W / 2 + 2, 9, 3.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#d8e070';
    ctx.fillRect(W / 2 + 2.6, 8.2, 1.3, 1.1);
    ctx.fillRect(W / 2 + 4.6, 8.2, 1.1, 1.1);
    // Arms reaching out.
    ctx.strokeStyle = '#8f9c79';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(W / 2 + 4, 14);
    ctx.lineTo(W / 2 + 10, 16);
    ctx.moveTo(W / 2 - 2, 14.5);
    ctx.lineTo(W / 2 + 7, 18);
    ctx.stroke();
  },
};
